import { BufferGeometry } from 'three';
import { Attr, AttrMap, IndexedMesh }  from "./indexed-mesh";
import { IndexedMeshWriter } from './indexed-mesh-writer';

export type AnyAttr = {
    position?: Attr
    uv?: Attr,
    normal?: Attr,
}

/**
 * A ThreeJSMesh is a MeshWriter that sources mesh data from a
 * BufferGeometry instance. It also provides a static method to create a
 * BufferGeometry from generated mesh data.
 * @param {BufferGeometry} geo - The BufferGeometry instance. This is
 * converted to non-indexed form (if required), otherwise the class retains a
 * copy of the given geometry. If you want to mutate the original, be aware that
 * that mutation will be observed when `attr` or `write` are called.
 */

export class IndexedThreeJSMesh<T extends AttrMap> extends IndexedMeshWriter<T> {
    geo: BufferGeometry;

    constructor(geo: BufferGeometry) {
        super();
        this.geo = geo;
    }

    computeAttributeCounts(): IndexedMesh<T> {
        const
            attrs = {} as AttrMap,
            result = new IndexedMesh<T>(attrs as T);

        result.index.count = this.geo.index.array.length;
        for(const name of Object.keys(this.geo.attributes)) {
            const buffattr = this.geo.getAttribute(name);
            switch(name) {
                case "position":
                    result.position.count = buffattr.array.length / buffattr.itemSize;
                    break;
                default: {
                    attrs[name] = new Attr(buffattr.itemSize);
                    attrs[name].count = buffattr.array.length / buffattr.itemSize;
                    break;
                }
            }
        }

        return result;
    }

    computeMeshData(result: IndexedMesh<T>) {
        result.index.data.set(this.geo.index.array, 0);
        result.position.data.set(this.geo.attributes.position.array, 0);

        for(const name of Object.keys(result.attrs)) {
            // The result may have, e.g., UVs that are not present in the
            // source three.js BufferGeometry instance, so we check before
            // attempting to copy the data. Skipping this will leave all zeroes
            // in the result data, which is probably fine in the case of UVs,
            // but may cause visual glitches for normals.
            if(name in this.geo.attributes) {
                result.attrs[name].data.set(this.geo.attributes[name].array, 0);
            }
        }
    }

}
